import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiX, FiClock } from 'react-icons/fi';
import { Spinner } from '@daycal/ui';
import { api } from '../lib/apiClient.js';
import EventAvailabilityFields, { rowsFromRules, rulesFromRows, DEFAULT_AVAIL_ROWS } from './EventAvailabilityFields.jsx';

/**
 * Standalone per-event availability editor (opened from the event card). Fetches the event's
 * current availability, lets the host edit it, and saves.
 * @param {{event: {id:string, title:string}, onClose: () => void}} props
 */
export default function EventAvailabilityModal({ event, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['event-availability', event.id],
    queryFn: () => api.getEventAvailability(event.id),
  });

  const [custom, setCustom] = useState(false);
  const [tz, setTz] = useState('Asia/Kolkata');
  const [rows, setRows] = useState(DEFAULT_AVAIL_ROWS);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!data) return;
    setCustom(!!data.custom);
    setTz(data.schedule?.timezone || 'Asia/Kolkata');
    setRows(rowsFromRules(data.rules));
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.updateEventAvailability(event.id, { custom, timezone: tz, rules: rulesFromRows(rows) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['event-availability', event.id] });
      qc.invalidateQueries({ queryKey: ['event-types'] });
      setCustom(!!res.custom);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2500);
    },
    onError: (err) => setMsg(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4" onClick={onClose}>
      <div className="my-8 w-full max-w-2xl rounded-dc bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <FiClock className="h-5 w-5 text-indigo-500" /> Availability
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              For <span className="font-semibold text-slate-700">{event.title}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-16"><Spinner /></div>
        ) : (
          <>
            <EventAvailabilityFields custom={custom} setCustom={setCustom} rows={rows} setRows={setRows} />
            <div className="mt-6 flex items-center justify-end gap-3">
              {msg && <span className={`text-sm font-medium ${msg === 'Saved' ? 'text-emerald-600' : 'text-red-600'}`}>{msg}</span>}
              <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
              <button type="button" onClick={() => save.mutate()} disabled={save.isPending}
                className="flex items-center justify-center rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-50">
                {save.isPending ? <Spinner /> : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
